import type {
  CodecEventRecord,
  CodecEventType,
  CodecMeshDeviceEventRecord,
  CodecMeshDeviceEventType,
} from "@irriga-mais/common";
import type { Dayjs } from "dayjs";
import type { DeviceMessageRequestPayloadType, DeviceMessageRequestStatus } from "@/api/model/device-message-request";
import { dayjs } from "@/utils/date";

/**
 * Raw `codec_event` row as returned by the history endpoint
 * (`GET /history/codec-event/by-codec/:id`), with dates as strings and an
 * untyped `event_state` (number or boolean depending on the event type).
 */
export type CodecEventTimelineRow = CodecEventRecord<{ dateType: string; eventStateType: unknown }>;

/**
 * Raw `codec_mesh_device_event` row as returned by the history endpoint
 * (`GET /history/codec-mesh-device-event/by-codec/:id`).
 */
export type MeshDeviceEventTimelineRow = CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: unknown }>;

/**
 * The source a timeline entry originates from. The timeline merges LIC-level
 * events, mesh device events and outgoing message requests.
 */
export type CodecTimelineSource = "codecEvent" | "meshEvent" | "messageRequest";

/**
 * A single entry in the CODEC timeline: a LIC-level device event, a mesh
 * device event, or an outgoing message request. Cards switch on `source`.
 */
export type CodecTimelineEntry = CodecEventTimelineEntry | MeshEventTimelineEntry | MessageRequestTimelineEntry;

/**
 * pt-BR labels for every message request payload type.
 */
export const MESSAGE_REQUEST_PAYLOAD_LABELS: Record<DeviceMessageRequestPayloadType, string> = {
  config: "Configuração",
  devices: "Dispositivos",
  scheduling: "Agendamento",
  dev_scheduling: "Agendamento (dev)",
  automation: "Automação",
  control: "Controle",
  command: "Comando",
  request_info: "Solicitação de informações",
  firmware_update: "Atualização de firmware",
};

/**
 * pt-BR labels for every message request delivery status.
 */
export const MESSAGE_REQUEST_STATUS_LABELS: Record<DeviceMessageRequestStatus, string> = {
  pending: "Pendente",
  processing: "Processando",
  sent: "Enviado",
  acknowledged: "Confirmado",
  failed: "Falhou",
  expired: "Expirado",
  cancelled: "Cancelado",
};

/**
 * Raw `device_message_request` row: the subset the timeline shows. The binary
 * `payload_bytes` column is deliberately not fetched.
 */
export interface MessageRequestTimelineRow {
  id: string;
  payload_type: DeviceMessageRequestPayloadType;
  status: DeviceMessageRequestStatus;
  scheduled_at: string;
  sent_at: string | null;
  acknowledged_at: string | null;
  attempts: number | null;
  max_attempts: number | null;
  last_error: string | null;
}

/**
 * A single entry in the CODEC timeline built from an outgoing message request,
 * anchored at `scheduled_at` (when the command entered the queue).
 */
export interface MessageRequestTimelineEntry {
  /** Stable unique key across sources and pages ("messageRequest:<id>"). */
  key: string;
  /** Source identifier. */
  source: "messageRequest";
  /** Raw payload type as stored in the message request. */
  payloadType: DeviceMessageRequestPayloadType;
  /** Raw delivery status. */
  status: DeviceMessageRequestStatus;
  /** pt-BR payload type label (e.g. "Controle"). */
  label: string;
  /** pt-BR delivery status label (e.g. "Enviado"). */
  statusLabel: string;
  /** Anchor timestamp for sorting and day grouping: the scheduled time (ISO). */
  startTime: string;
  /** Scheduled time the command entered the queue (ISO). */
  scheduledAt: string;
  /** Time the message was sent to the device, or null. */
  sentAt: string | null;
  /** Time the device acknowledged the message, or null. */
  acknowledgedAt: string | null;
  /** Delivery attempts so far. */
  attempts: number | null;
  /** Maximum delivery attempts. */
  maxAttempts: number | null;
  /** Last delivery error message, or null when absent. */
  lastError: string | null;
}

/** pt-BR labels for every LIC-level device event type. */
export const CODEC_EVENT_LABELS: Record<CodecEventType, string> = {
  rainfall: "Chuva acumulada",
  rainfall_global_count: "Contagem de chuva",
  resets: "Reinicializações",
  raining: "Chuva detectada",
  scheduling_running: "Agendamento em execução",
  scheduling_paused: "Agendamento pausado",
  input_voltage: "Tensão de entrada",
  analog_voltage: "Entrada analógica",
};

/**
 * A single entry in the CODEC timeline built from a LIC-level device event.
 */
export interface CodecEventTimelineEntry {
  /** Stable unique key across sources and pages ("codecEvent:raining:2026-..."). */
  key: string;
  /** Source identifier. */
  source: "codecEvent";
  /** Raw event type as stored by the device event handler. */
  eventType: CodecEventType;
  /** pt-BR event label (e.g. "Chuva acumulada"). */
  label: string;
  /** Human-readable event value (e.g. "2 mm", "Ativo", "24000 mV"). */
  valueLabel: string;
  /** Anchor timestamp used for sorting and day grouping (ISO string). */
  startTime: string;
  /** End timestamp (ISO string) or null while the event is ongoing. */
  endTime: string | null;
  /** True when the event has no end time yet. */
  isOngoing: boolean;
  /** Duration in seconds, measured up to `now` for ongoing events. */
  durationSeconds: number;
  /** Raw event state kept for forensics (never displayed directly). */
  rawState: unknown;
}

const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * Formats a number using the Brazilian decimal separator ("12.5" → "12,5");
 * other values are stringified as-is.
 *
 * @param value - Value to format.
 * @returns The formatted string.
 */
function formatNumberValue(value: unknown): string {
  if (isFiniteNumber(value)) {
    const raw = String(value);
    return raw.replace(".", ",").replace("-", "−");
  }
  return String(value ?? "");
}

/**
 * Returns the human-readable value for a LIC-level device event: counts as
 * plain numbers, rainfall in mm, voltages in mV (with the converted analog
 * reading when present), booleans as Sim/Não or Ativo/Inativo per event type.
 *
 * @param eventType - The event type.
 * @param eventState - The raw event state (number or boolean).
 * @param metadata - Optional payload metadata (analog converted value).
 * @returns The pt-BR display value.
 */
export function formatCodecEventValue(eventType: CodecEventType, eventState: unknown, metadata: unknown): string {
  switch (eventType) {
    case "raining":
      return isBoolean(eventState) ? (eventState ? "Sim" : "Não") : String(eventState ?? "");
    case "scheduling_running":
    case "scheduling_paused":
      return isBoolean(eventState) ? (eventState ? "Ativo" : "Inativo") : String(eventState ?? "");
    case "rainfall":
      return `${formatNumberValue(eventState)} mm`;
    case "input_voltage":
      return `${formatNumberValue(eventState)} mV`;
    case "analog_voltage": {
      const raw = `${formatNumberValue(eventState)} mV`;
      if (isFiniteNumber(metadata)) {
        return `${raw} · convertido: ${formatNumberValue(metadata)}`;
      }
      return raw;
    }
    case "rainfall_global_count":
    case "resets":
      return formatNumberValue(eventState);
    default:
      return formatNumberValue(eventState);
  }
}

/**
 * Parsed CODEC timeline URL parameters. `undefined` means the parameter was
 * absent from the URL and the current filter should be kept; `null` means the
 * parameter was present but empty (clearing the filter).
 */
export interface CodecTimelineUrlParams {
  /** Selected codec id, null clears the selection, undefined keeps it. */
  codecId: string | null | undefined;
  /** Range start (ISO) or undefined when absent/invalid. */
  startDate: string | undefined;
  /** Range end (ISO) or undefined when absent/invalid. */
  endDate: string | undefined;
}

const readDateParam = (params: URLSearchParams, name: string): string | undefined => {
  const value = params.get(name);
  if (!value) return undefined;
  const date = dayjs(value);
  return date.isValid() ? date.toISOString() : undefined;
};

/**
 * Reads the deep-link URL parameters (`codecId`, `start`, `end`) for the CODEC
 * timeline page. Absent values stay `undefined` so the caller can keep the
 * current filter; invalid dates are ignored.
 *
 * @param params - The current URL search params.
 * @returns The parsed values, or `undefined` per field when absent/invalid.
 */
export function readCodecTimelineUrlParams(params: URLSearchParams): CodecTimelineUrlParams {
  return {
    codecId: params.has("codecId") ? params.get("codecId") || null : undefined,
    startDate: readDateParam(params, "start"),
    endDate: readDateParam(params, "end"),
  };
}

/**
 * Resolves a nullable end timestamp to an ISO string, or null when absent or
 * unparsable.
 *
 * @param value - The raw end timestamp from the device event row.
 * @returns The normalized ISO string, or null.
 */
function toEndTimestamp(value: string | null): string | null {
  if (!value) return null;
  const end = dayjs(value);
  return end.isValid() ? end.toISOString() : null;
}

/**
 * Transforms raw LIC-level device event rows into timeline entries, sorted
 * newest first by start time. Rows without a parseable start time are
 * skipped — they carry no anchor and cannot be placed on the timeline.
 *
 * @param rows - Raw rows from the codec event history endpoint.
 * @param now - Reference moment for ongoing-event durations (defaults to now).
 * @returns Timeline entries in newest-first order.
 */
export function codecEventsToTimelineEntries(
  rows: CodecEventTimelineRow[],
  now: Dayjs = dayjs()
): CodecEventTimelineEntry[] {
  const entries: CodecEventTimelineEntry[] = [];

  for (const row of rows) {
    const start = dayjs(row.start_time);
    if (!start.isValid()) {
      continue;
    }
    const startTime = start.toISOString();
    const endIso = toEndTimestamp(row.end_time);
    const durationMs = endIso ? dayjs(endIso).diff(start) : now.diff(start);
    const durationSeconds = Math.max(0, Math.floor(durationMs / 1000));

    entries.push({
      key: `codecEvent:${row.event_type}:${start.toISOString()}`,
      source: "codecEvent",
      eventType: row.event_type,
      label: CODEC_EVENT_LABELS[row.event_type] ?? row.event_type,
      valueLabel: formatCodecEventValue(row.event_type, row.event_state, row.metadata),
      startTime,
      endTime: endIso,
      isOngoing: endIso === null,
      durationSeconds,
      rawState: row.event_state,
    });
  }

  return entries.sort((a, b) => b.startTime.localeCompare(a.startTime));
}

/** pt-BR labels for every mesh device event type. */
export const MESH_EVENT_LABELS: Record<CodecMeshDeviceEventType, string> = {
  on: "Saída",
  sync: "Sincronização",
  input: "Entrada",
  failed: "Falha",
};

/** Boolean display pairs [true, false] for each mesh event type. */
const MESH_BOOLEAN_VALUES: Record<CodecMeshDeviceEventType, [string, string]> = {
  on: ["Ligada", "Desligada"],
  sync: ["Sincronizado", "Não sincronizado"],
  input: ["Ativa", "Inativa"],
  failed: ["Sim", "Não"],
};

/**
 * A single entry in the CODEC timeline built from a mesh device event. The
 * device identifier and associated element (sector or pump) are resolved from
 * the row's denormalized associations via {@link MeshEventLabelMaps}.
 */
export interface MeshEventTimelineEntry {
  /** Stable unique key across sources and pages ("meshEvent:<device>:<idx>:<type>:<start>"). */
  key: string;
  /** Source identifier. */
  source: "meshEvent";
  /** Raw event type as stored by the device event handler. */
  eventType: CodecMeshDeviceEventType;
  /** pt-BR event label (e.g. "Saída"). */
  label: string;
  /** Human-readable event value (e.g. "Ligada", "Sincronizado"). */
  valueLabel: string;
  /** Anchor timestamp used for sorting and day grouping (ISO string). */
  startTime: string;
  /** End timestamp (ISO string) or null while the event is ongoing. */
  endTime: string | null;
  /** True when the event has no end time yet. */
  isOngoing: boolean;
  /** Duration in seconds, measured up to `now` for ongoing events. */
  durationSeconds: number;
  /** Raw event state kept for forensics (never displayed directly). */
  rawState: unknown;
  /** The mesh device id that reported the event. */
  meshDeviceId: string;
  /** Mesh device identifier (e.g. "555556"), or null when unknown. */
  deviceIdentifier: string | null;
  /** Element label (e.g. "Setor 4", "Bomba Principal"), or null when unresolvable. */
  elementName: string | null;
}

/**
 * Name maps used to label mesh events: device identifiers, sector names and
 * water pump names, keyed by id. Built from the loaded domain data.
 */
export interface MeshEventLabelMaps {
  deviceIdentifierById: ReadonlyMap<string, string>;
  sectorNameById: ReadonlyMap<string, string>;
  waterPumpNameById: ReadonlyMap<string, string>;
}

/**
 * Returns the human-readable value for a mesh device event: booleans as
 * Ligada/Desligada (outputs), Sincronizado/Não sincronizado (sync), Ativa/
 * Inativa (inputs) or Sim/Não (failures).
 *
 * @param eventType - The mesh event type.
 * @param eventState - The raw boolean state.
 * @returns The pt-BR display value.
 */
export function formatMeshEventValue(eventType: CodecMeshDeviceEventType, eventState: unknown): string {
  if (!isBoolean(eventState)) {
    return String(eventState ?? "");
  }
  const values = MESH_BOOLEAN_VALUES[eventType];
  return values ? (eventState ? values[0] : values[1]) : String(eventState);
}

function formatElementName(kind: "Setor" | "Bomba", name: string): string {
  const lower = name.toLowerCase();
  return lower.startsWith("setor") || lower.startsWith("bomba") ? name : `${kind} ${name}`;
}

/**
 * Resolves the element label for a mesh event from its denormalized sector or
 * water pump association.
 *
 * @param maps - The label maps.
 * @param sectorId - The denormalized sector id, or null.
 * @param waterPumpId - The denormalized water pump id, or null.
 * @returns "Setor …" / pump name, or null when unresolvable.
 */
export function resolveMeshElementLabel(
  maps: MeshEventLabelMaps,
  sectorId: string | null,
  waterPumpId: string | null
): string | null {
  if (sectorId) {
    const name = maps.sectorNameById.get(sectorId);
    return name ? formatElementName("Setor", name) : null;
  }
  if (waterPumpId) {
    const name = maps.waterPumpNameById.get(waterPumpId);
    return name ? formatElementName("Bomba", name) : null;
  }
  return null;
}

/**
 * Transforms raw mesh device event rows into timeline entries, sorted newest
 * first by start time. Rows without a parseable start time are skipped.
 *
 * @param rows - Raw rows from the mesh device event history endpoint.
 * @param maps - Name maps for device/element labels.
 * @param now - Reference moment for ongoing-event durations (defaults to now).
 * @returns Mesh timeline entries in newest-first order.
 */
export function meshDeviceEventsToTimelineEntries(
  rows: MeshDeviceEventTimelineRow[],
  maps: MeshEventLabelMaps,
  now: Dayjs = dayjs()
): MeshEventTimelineEntry[] {
  const entries: MeshEventTimelineEntry[] = [];

  for (const row of rows) {
    const start = dayjs(row.start_time);
    if (!start.isValid()) {
      continue;
    }
    const startTime = start.toISOString();
    const endIso = toEndTimestamp(row.end_time);
    const durationMs = endIso ? dayjs(endIso).diff(start) : now.diff(start);

    entries.push({
      key: `meshEvent:${row.mesh_device}:${row.device_idx}:${row.event_type}:${startTime}`,
      source: "meshEvent",
      eventType: row.event_type,
      label: MESH_EVENT_LABELS[row.event_type] ?? row.event_type,
      valueLabel: formatMeshEventValue(row.event_type, row.event_state),
      startTime,
      endTime: endIso,
      isOngoing: endIso === null,
      durationSeconds: Math.max(0, Math.floor(durationMs / 1000)),
      rawState: row.event_state,
      meshDeviceId: row.mesh_device,
      deviceIdentifier: maps.deviceIdentifierById.get(row.mesh_device) ?? null,
      elementName: resolveMeshElementLabel(maps, row.sector, row.water_pump),
    });
  }

  return entries.sort((a, b) => b.startTime.localeCompare(a.startTime));
}

const SOURCE_PRIORITY: Record<CodecTimelineSource, number> = {
  codecEvent: 0,
  meshEvent: 1,
  messageRequest: 2,
};

/**
 * Transforms raw message request rows into timeline entries, sorted newest
 * first by scheduled time. Rows without a parseable `scheduled_at` are
 * skipped — they have no anchor and cannot be placed on the timeline.
 *
 * @param rows - Raw rows from the device_message_request collection.
 * @returns Message request timeline entries in newest-first order.
 */
export function messageRequestsToTimelineEntries(rows: MessageRequestTimelineRow[]): MessageRequestTimelineEntry[] {
  const entries: MessageRequestTimelineEntry[] = [];

  for (const row of rows) {
    const scheduled = dayjs(row.scheduled_at);
    if (!scheduled.isValid()) {
      continue;
    }
    const scheduledAt = scheduled.toISOString();
    const statusLabel = MESSAGE_REQUEST_STATUS_LABELS[row.status] ?? row.status;

    entries.push({
      key: `messageRequest:${row.id}`,
      source: "messageRequest",
      payloadType: row.payload_type,
      status: row.status,
      label: MESSAGE_REQUEST_PAYLOAD_LABELS[row.payload_type] ?? row.payload_type,
      statusLabel,
      startTime: scheduledAt,
      scheduledAt,
      sentAt: row.sent_at,
      acknowledgedAt: row.acknowledged_at,
      attempts: row.attempts,
      maxAttempts: row.max_attempts,
      lastError: row.last_error,
    });
  }

  return entries.sort((a, b) => b.startTime.localeCompare(a.startTime));
}

/**
 * Merges per-source entry lists into a single timeline sorted newest first.
 * Entries sharing the same anchor are ordered by source priority (LIC events
 * first) and then by entry key, giving a stable tie-break.
 *
 * @param entryLists - One entry list per source (already internally sorted).
 * @returns The interleaved, sorted timeline entries.
 */
export function mergeCodecTimelineSources(entryLists: CodecTimelineEntry[][]): CodecTimelineEntry[] {
  return entryLists.flat().sort((a, b) => {
    const byTime = b.startTime.localeCompare(a.startTime);
    if (byTime !== 0) return byTime;
    const bySource = SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source];
    if (bySource !== 0) return bySource;
    return a.key.localeCompare(b.key);
  });
}

/**
 * Per-source pagination state for the CODEC timeline: how many pages have
 * been fetched from each source, and whether it has no more rows for the
 * current range.
 */
export interface CodecTimelineSourceProgress {
  /** Pages already fetched from this source (next fetch starts here). */
  offset: number;
  /** True when this source returned fewer than a full page (no more rows). */
  exhausted: boolean;
}

/** Pagination state for all three timeline sources. */
export type CodecTimelineSourceProgressMap = Record<CodecTimelineSourceKey, CodecTimelineSourceProgress>;

/** The three timeline sources, used as the pagination record keys. */
export type CodecTimelineSourceKey = "codecEvent" | "meshEvent" | "messageRequest";

/**
 * Creates the initial pagination state: every source open at offset zero.
 *
 * @returns The initial progress map.
 */
export function createInitialCodecTimelineSourceProgress(): CodecTimelineSourceProgressMap {
  return {
    codecEvent: { offset: 0, exhausted: false },
    meshEvent: { offset: 0, exhausted: false },
    messageRequest: { offset: 0, exhausted: false },
  };
}

/**
 * Returns true while at least one source still has more pages to load.
 *
 * @param progress - The per-source pagination state.
 * @returns Whether any source is still open.
 */
export function hasAnyCodecTimelineSourceMore(progress: CodecTimelineSourceProgressMap): boolean {
  return !progress.codecEvent.exhausted || !progress.meshEvent.exhausted || !progress.messageRequest.exhausted;
}

/**
 * Advances the pagination state after a batch of source fetches. A source
 * that returned a full page keeps its offset advanced by the page size; a
 * source that returned fewer rows is marked exhausted (nothing more to
 * load). Sources without a result are left untouched.
 *
 * @param progress - The current pagination state.
 * @param fetchedCounts - Rows returned per fetched source.
 * @param limit - The page size used for the fetches.
 * @returns The next pagination state.
 */
export function advanceCodecTimelineSources(
  progress: CodecTimelineSourceProgressMap,
  fetchedCounts: Partial<Record<CodecTimelineSourceKey, number>>,
  limit: number
): CodecTimelineSourceProgressMap {
  const next: CodecTimelineSourceProgressMap = {
    codecEvent: { ...progress.codecEvent },
    meshEvent: { ...progress.meshEvent },
    messageRequest: { ...progress.messageRequest },
  };

  for (const source of Object.keys(fetchedCounts) as CodecTimelineSourceKey[]) {
    const count = fetchedCounts[source];
    if (count === undefined || next[source].exhausted) {
      continue;
    }
    if (count < limit) {
      next[source].exhausted = true;
    } else {
      next[source].offset += limit;
    }
  }

  return next;
}
